import { motion } from 'framer-motion';
import { Info as InformationCircleIcon } from 'lucide-react';
import React, { useState } from 'react';
import {
  TimeBucket,
  TimeDistributionData,
  formatTimeDuration,
  getCountBasedColor,
  getMaxCount,
  getSortedBucketKeys,
} from './timeDistributionUtils';

interface SingleTimeChartProps {
  type: 'response' | 'resolution';
  data: TimeDistributionData;
  buckets: Record<string, TimeBucket>;
  title: string;
  icon: React.ReactNode;
  info?: string;
  query?: string;
}

interface TooltipData {
  bucketKey: string;
  bucket: TimeBucket;
  data: { count: number; percentage: number; avgTime?: number };
  x: number;
  y: number;
  barColor: string; // The calculated count-based color
}

const SingleTimeChart: React.FC<SingleTimeChartProps> = ({
  type,
  data,
  buckets,
  title,
  icon,
  info,
  query,
}) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);

  const maxCount = getMaxCount(data);
  const sortedBucketKeys = getSortedBucketKeys(buckets);

  // Info tooltip content based on chart type
  const getInfoTooltipContent = () => {
    if (type === 'response') {
      return {
        title: 'First Response Time Data',
        content:
          info ||
          "These are cases where first response time is recorded. Some cases might have been resolved in first touch and won't be counted here.",
      };
    } else {
      return {
        title: 'Resolution Time Data',
        content:
          info ||
          'These are cases that have been resolved (Status = "Closed"). Cases with other statuses like "Pending Bug", "Responded", "In Progress", or "New" are not counted here.',
      };
    }
  };

  const handleBarHover = (bucketKey: string, event: React.MouseEvent<HTMLDivElement>) => {
    const barElement = event.currentTarget;
    const chartContainer = barElement.closest('.chart-container') as HTMLElement;

    if (chartContainer) {
      // Get the bar's position within the chart container
      const barRect = barElement.getBoundingClientRect();
      const containerRect = chartContainer.getBoundingClientRect();

      // Calculate center position of the bar relative to container
      const centerX = barRect.left - containerRect.left + barRect.width / 2;
      const topY = barRect.top - containerRect.top;

      // Smart positioning to prevent cutoff
      const tooltipWidth = 200; // Estimated tooltip width
      const containerWidth = containerRect.width;

      let adjustedX = centerX;

      // If tooltip would go off the right edge, adjust it
      if (centerX + tooltipWidth / 2 > containerWidth - 20) {
        adjustedX = containerWidth - tooltipWidth / 2 - 20;
      }
      // If tooltip would go off the left edge, adjust it
      else if (centerX - tooltipWidth / 2 < 20) {
        adjustedX = tooltipWidth / 2 + 20;
      }

      // Calculate the count-based color for this bar
      const baseColor = type === 'response' ? 'green' : 'blue';
      const bucketData = data[bucketKey];
      const barColor = getCountBasedColor(bucketData.count, maxCount, baseColor);

      setTooltip({
        bucketKey,
        bucket: buckets[bucketKey],
        data: data[bucketKey],
        x: adjustedX,
        y: topY - 20, // Position above the bar
        barColor: barColor,
      });
    }

    setHoveredBar(bucketKey);
  };

  const handleBarLeave = () => {
    setTooltip(null);
    setHoveredBar(null);
  };

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 chart-container">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <div className="relative">
              <motion.button
                onMouseEnter={() => setShowInfoTooltip(true)}
                onMouseLeave={() => setShowInfoTooltip(false)}
                className="group relative flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 hover:from-blue-200 hover:to-indigo-200 dark:hover:from-blue-800/40 dark:hover:to-indigo-800/40 transition-all duration-300 shadow-sm hover:shadow-md"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <InformationCircleIcon className="w-3 h-3 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200" />

                {/* Subtle glow effect */}
                <div className="absolute inset-0 rounded-full bg-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
              </motion.button>

              {/* Modern Info Tooltip */}
              {showInfoTooltip && (
                <motion.div
                  className="absolute left-8 top-0 z-50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-5 py-4 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 pointer-events-none w-80 backdrop-blur-sm"
                  initial={{ opacity: 0, scale: 0.9, x: -10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9, x: -10 }}
                  transition={{ duration: 0.3, type: 'spring', stiffness: 200, damping: 20 }}
                >
                  {/* Gradient header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
                    <div className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                      {getInfoTooltipContent().title}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                    {getInfoTooltipContent().content}
                    {query && (
                      <>
                        <pre className="mt-2 max-h-56 overflow-auto whitespace-pre rounded bg-black/30 p-2 text-[11px] font-mono">
                          {query}
                        </pre>
                        <p className="mt-1 text-[10px] opacity-70">
                          Replace {'{startDate}'} / {'{endDate}'} with your range (YYYY-MM-DD).
                        </p>
                      </>
                    )}
                  </div>

                  {/* Modern arrow */}
                  <div className="absolute left-[-8px] top-4 w-0 h-0 border-t-[8px] border-b-[8px] border-r-[8px] border-transparent border-r-white dark:border-r-gray-800" />
                  <div className="absolute left-[-9px] top-4 w-0 h-0 border-t-[8px] border-b-[8px] border-r-[8px] border-transparent border-r-gray-200 dark:border-r-gray-600" />
                </motion.div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Distribution by time ranges</p>
        </div>
      </div>

      {/* Chart Area */}
      <div className="relative">
        {/* Chart with bars - height reduced to make room for labels */}
        <div className="relative h-80">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
            <span>{maxCount}</span>
            <span>{Math.round(maxCount * 0.75)}</span>
            <span>{Math.round(maxCount * 0.5)}</span>
            <span>{Math.round(maxCount * 0.25)}</span>
            <span>0</span>
          </div>

          {/* Chart container - bars only */}
          <div className="ml-8 h-full flex items-end justify-between gap-2 border-l border-b border-gray-200 dark:border-gray-600 pl-4 pb-4">
            {sortedBucketKeys.map((bucketKey, index) => {
              const bucketData = data[bucketKey];
              const bucket = buckets[bucketKey];
              const barHeight = maxCount > 0 ? (bucketData.count / maxCount) * 100 : 0;
              const isHovered = hoveredBar === bucketKey;

              // Get count-based color (darker for higher counts, lighter for lower counts)
              const baseColor = type === 'response' ? 'green' : 'blue';
              const barColor = getCountBasedColor(bucketData.count, maxCount, baseColor);

              return (
                <div key={bucketKey} className="flex-1 flex flex-col items-center">
                  {/* Bar */}
                  <motion.div
                    className="w-full relative"
                    style={{ height: '280px' }}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.6,
                      type: 'spring',
                      stiffness: 100,
                    }}
                  >
                    <motion.div
                      className="absolute bottom-0 w-full rounded-t-lg shadow-lg cursor-pointer"
                      style={{
                        height: `${barHeight}%`,
                        backgroundColor: barColor,
                        transformOrigin: 'bottom',
                      }}
                      onMouseEnter={e => handleBarHover(bucketKey, e)}
                      onMouseLeave={handleBarLeave}
                      whileHover={{
                        scale: 1.05,
                        boxShadow: `0 8px 25px -8px ${barColor}80`,
                      }}
                      animate={{
                        opacity: isHovered ? 1 : hoveredBar ? 0.6 : 1,
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        {/* X-axis labels - positioned below the chart */}
        <div className="ml-8 flex justify-between gap-2 pl-4 pt-3">
          {sortedBucketKeys.map(bucketKey => {
            const bucketData = data[bucketKey];
            const bucket = buckets[bucketKey];

            return (
              <div key={bucketKey} className="flex-1 flex flex-col items-center">
                {/* X-axis label */}
                <div className="text-xs text-gray-600 dark:text-gray-300 text-center font-medium">
                  {bucket.label}
                </div>

                {/* Count display */}
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                  {bucketData.count}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <motion.div
          className="absolute z-50 bg-gray-900 dark:bg-gray-700 text-white px-4 py-3 rounded-lg shadow-xl border border-gray-600 pointer-events-none min-w-[180px]"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%) translateY(-100%)',
          }}
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 10 }}
          transition={{ duration: 0.2, type: 'spring', stiffness: 300 }}
        >
          <div
            className="text-sm font-semibold mb-2 text-center"
            style={{ color: tooltip.barColor }}
          >
            {tooltip.bucket.label}
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">Cases:</span>
              <span className="font-medium text-white">{tooltip.data.count}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">Percentage:</span>
              <span className="font-medium text-white">{tooltip.data.percentage.toFixed(1)}%</span>
            </div>
            {tooltip.data.avgTime && tooltip.data.avgTime > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-300">Avg Time:</span>
                <span className="font-medium text-white">
                  {formatTimeDuration(tooltip.data.avgTime)}
                </span>
              </div>
            )}
          </div>
          {/* Tooltip arrow pointing down to the bar */}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
            style={{ borderTopColor: tooltip.barColor }}
          />
        </motion.div>
      )}
    </div>
  );
};

export default SingleTimeChart;
